import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Bot,
  Building2,
  CalendarCheck,
  FileSignature,
  FileText,
  IdCard,
  Landmark,
  MapPin,
  Receipt,
  ReceiptText,
  Scale,
  Send,
  Settings,
  Stamp,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { SetupChecklist, type SetupStep } from '@/components/SetupChecklist'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ReportRunner } from '@/components/ReportRunner'
import { TAX_REPORTS } from '@/features/tax/reports'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SimpleListPage } from '@/components/SimpleListPage'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { useAuth } from '@/auth/useAuth'
import {
  useTaxDashboardTiles,
  useFilings,
  useTaxPayments,
  useTaxCalendar,
  useTaxJurisdictions,
  useNexusObservations,
  useExemptionCerts,
  useReconciliations,
  useTaxDocuments,
  useTaxpayers,
  useAIRecommendations,
  useDisclaimer,
  useTaxRegistrations,
  rateLookup,
  askTaxAssistant,
  releasePayment,
  runReconciliation,
  generateCalendar,
  type TaxFiling,
  type RateLookupResult,
} from '@/features/tax/extras'
import {
  GenerateFilingModal,
  TaxpayerFormModal,
  RegistrationFormModal,
  SchedulePaymentModal,
  ExemptionCertModal,
  type FilingKind,
} from '@/features/tax/TaxModals'
import { Plus, RefreshCw, Pencil } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'outline' | 'danger' | 'info'> = {
  draft: 'outline', generated: 'info', reviewed: 'info', signed: 'info',
  submitted: 'info', acknowledged: 'info', filed: 'success',
  amended: 'warning', rejected: 'danger', expired: 'secondary',
}

const FILING_LABEL: Record<string, string> = {
  us_941: 'Form 941 (Quarterly federal payroll)',
  us_940: 'Form 940 (Annual FUTA)',
  us_944: 'Form 944 (Annual)',
  us_w2: 'W-2',
  us_w3: 'W-3 Transmittal',
  us_1099_nec: '1099-NEC',
  us_1099_misc: '1099-MISC',
  us_1099_div: '1099-DIV',
  us_1099_int: '1099-INT',
  us_1099_k: '1099-K',
  us_1095c: '1095-C',
  us_1094c: '1094-C Transmittal',
  us_1040: 'Form 1040 (Personal)',
  us_1120: 'Form 1120 (C-corp)',
  us_1120s: 'Form 1120-S (S-corp)',
  us_1065: 'Form 1065 (Partnership)',
  state_payroll_quarterly: 'State payroll quarterly',
  state_payroll_annual: 'State payroll annual',
  state_w2: 'State W-2 transmittal',
  state_sales_tax: 'State sales tax return',
  state_use_tax: 'State use tax',
  state_business_income: 'State business income',
  local_payroll: 'Local payroll filing',
}

function FilingTypeCell({ row }: { row: { original: { filing_type: string } } }) {
  return <span>{FILING_LABEL[row.original.filing_type] ?? row.original.filing_type}</span>
}

function StatusCell({ row }: { row: { original: { status: string } } }) {
  return <Badge variant={STATUS_VARIANT[row.original.status] || 'secondary'}>{row.original.status}</Badge>
}

function DateCell(field: string) {
  return ({ row }: { row: { original: Record<string, unknown> } }) => {
    const value = row.original[field] as string | null | undefined
    return value ? formatDate(value) : '-'
  }
}

function MoneyCell(amountField: string, currencyField = 'currency') {
  return ({ row }: { row: { original: Record<string, unknown> } }) => {
    const v = row.original[amountField] as string | null
    if (!v) return '-'
    return formatPayrollMoney(v, (row.original[currencyField] as string) || 'USD')
  }
}

function GenerateFilingActions({ kinds }: { kinds: Array<{ kind: FilingKind; label: string }> }) {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [openKind, setOpenKind] = useState<FilingKind | null>(null)
  if (!hasPermission('tax.filings.draft')) return null
  return (
    <Toolbar>
      {kinds.map((k) => (
        <Button key={k.kind} variant={k.kind === kinds[0].kind ? 'primary' : 'secondary'} onClick={() => setOpenKind(k.kind)}>
          <Plus className="mr-2 size-4" /> {k.label}
        </Button>
      ))}
      {openKind ? (
        <GenerateFilingModal
          open={!!openKind}
          onOpenChange={(v) => { if (!v) setOpenKind(null) }}
          kind={openKind}
          onGenerated={(id) => { setOpenKind(null); if (id) navigate(`/tax/filings/detail/${id}`) }}
        />
      ) : null}
    </Toolbar>
  )
}

function ViewFilingCell({ row }: { row: { original: { id: string } } }) {
  const navigate = useNavigate()
  return (
    <Button size="sm" variant="ghost" onClick={() => navigate(`/tax/filings/detail/${row.original.id}`)}>
      View
    </Button>
  )
}

function Tile({
  icon: Icon, label, value, hint, accent,
}: { icon: typeof Receipt; label: string; value: string | number; hint?: string; accent?: 'default' | 'warning' | 'success' | 'danger' }) {
  const accentClass = accent === 'warning' ? 'text-amber-500'
    : accent === 'success' ? 'text-emerald-500'
    : accent === 'danger' ? 'text-destructive'
    : 'text-primary'
  return (
    <Card>
      <CardContent className="flex items-start gap-4 p-5">
        <div className={`mt-1 ${accentClass}`}><Icon className="size-6" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
          {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
        </div>
      </CardContent>
    </Card>
  )
}

export function TaxDashboardPage() {
  const { data, isLoading, isError, error, refetch } = useTaxDashboardTiles()
  const tiles = data?.data
  const taxpayers = useTaxpayers()
  const registrations = useTaxRegistrations()
  const calendar = useTaxCalendar()
  const filings = useFilings({})
  const steps: SetupStep[] = [
    { title: 'Add a taxpayer', description: 'Register the legal entity that files returns.', to: '/tax/setup', cta: 'Add taxpayer', done: (taxpayers.data?.data?.length ?? 0) > 0 },
    { title: 'Register jurisdictions', description: 'Add the federal, state, and local agencies you file with.', to: '/tax/setup?tab=jurisdictions', cta: 'Register', done: (registrations.data?.data?.length ?? 0) > 0 },
    { title: 'Generate your tax calendar', description: 'Build the due-date schedule from your registrations.', to: '/tax/calendar', cta: 'Generate', done: (calendar.data?.data?.length ?? 0) > 0 },
    { title: 'Generate your first filing', description: 'Draft a 941, 940, W-2, or 1099 from your data.', to: '/tax/filings-hub', cta: 'Generate', done: (filings.data?.data?.length ?? 0) > 0 },
  ]
  return (
    <Page
      title="Tax"
      description="Payroll, sales, business income, and personal tax filings - driven by your existing accounting data. Every AI suggestion carries citations and goes through approval before filing."
    >
      <SetupChecklist title="Get started with Tax" steps={steps} />
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : isError || !tiles ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-base font-semibold text-destructive">Couldn&apos;t load Tax tiles</h2>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'Make sure the tax_os feature flag is enabled for your workspace.'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile icon={FileSignature} label="Open filings" value={tiles.open_filings} hint="Draft / generated / reviewed / signed" />
            <Tile
              icon={AlertTriangle}
              label="Overdue"
              value={tiles.overdue_count}
              accent={tiles.overdue_count > 0 ? 'danger' : 'success'}
              hint="Past due date, not filed"
            />
            <Tile
              icon={ReceiptText}
              label="YTD tax liability"
              value={formatPayrollMoney(tiles.ytd_liability_minor_units || '0')}
              hint="Sum of total_due across this year's filings"
            />
            <Tile icon={Landmark} label="Active registrations" value={tiles.active_registrations} hint="SUI / EIN / sales-tax permits" />
          </div>
          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="text-base font-semibold">Next filing due</h2>
              {tiles.next_due ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{FILING_LABEL[tiles.next_due.filing_type] ?? tiles.next_due.filing_type}</span>
                      <Badge variant={STATUS_VARIANT[tiles.next_due.status] || 'secondary'}>{tiles.next_due.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Period {formatDate(tiles.next_due.period_start)} → {formatDate(tiles.next_due.period_end)} · due {formatDate(tiles.next_due.due_date)}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No filings on the calendar. Once you register for federal/state withholding and SUI under Jurisdictions, the filing calendar will auto-generate.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  )
}

function FilingsListGeneric({
  title, description, filing_type_prefix, filing_type, actions,
}: { title: string; description: string; filing_type_prefix?: string; filing_type?: string; actions?: React.ReactNode }) {
  const { data, isLoading, error } = useFilings({ filing_type_prefix, filing_type })
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: TaxFiling } }) => unknown }>>(
    () => [
      { accessorKey: 'filing_type', header: 'Form', cell: FilingTypeCell },
      { accessorKey: 'tax_year', header: 'Year' },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }) => `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
      },
      { accessorKey: 'due_date', header: 'Due', cell: DateCell('due_date') as never },
      { accessorKey: 'total_due_minor_units', header: 'Liability', cell: MoneyCell('total_due_minor_units') as never },
      { accessorKey: 'status', header: 'Status', cell: StatusCell },
      { accessorKey: 'filed_at', header: 'Filed', cell: DateCell('filed_at') as never },
      { accessorKey: 'id', header: '', cell: ViewFilingCell as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title={title}
      description={description}
      icon={FileSignature}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={actions}
      emptyTitle="No filings yet"
      emptyDescription="Filings appear here once you generate a draft (manual or via the quarterly-close workflow)."
    />
  )
}

export function AllFilingsPage() {
  return (
    <FilingsListGeneric
      title="All Filings"
      description="Every tax filing across payroll, contractors, sales, business, personal, and state. Draft → reviewed → signed → submitted → acknowledged → filed."
      actions={
        <GenerateFilingActions
          kinds={[
            { kind: '941', label: 'Generate 941' },
            { kind: '940', label: 'Generate 940' },
            { kind: 'w2', label: 'W-2 batch' },
            { kind: '1099', label: '1099 batch' },
          ]}
        />
      }
    />
  )
}

export function PayrollFilingsPage() {
  const { data, isLoading, error } = useFilings({})
  const filtered = (data?.data ?? []).filter((f) =>
    ['us_941', 'us_940', 'us_944', 'us_w2', 'us_w3', 'state_payroll_quarterly', 'state_payroll_annual', 'state_w2', 'local_payroll'].includes(f.filing_type),
  )
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: TaxFiling } }) => unknown }>>(
    () => [
      { accessorKey: 'filing_type', header: 'Form', cell: FilingTypeCell },
      { accessorKey: 'tax_year', header: 'Year' },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }) => `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
      },
      { accessorKey: 'due_date', header: 'Due', cell: DateCell('due_date') as never },
      { accessorKey: 'total_due_minor_units', header: 'Liability', cell: MoneyCell('total_due_minor_units') as never },
      { accessorKey: 'status', header: 'Status', cell: StatusCell },
      { accessorKey: 'id', header: '', cell: ViewFilingCell as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Payroll Filings"
      description="Federal 941, 940, W-2/W-3 plus state quarterly + annual withholding/SUI and local payroll filings. Generated from posted pay runs."
      icon={FileSignature}
      rows={filtered}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={
        <GenerateFilingActions
          kinds={[
            { kind: '941', label: 'Generate 941' },
            { kind: '940', label: 'Generate 940' },
            { kind: 'w2', label: 'W-2 batch' },
          ]}
        />
      }
      emptyTitle="No payroll filings yet"
      emptyDescription="Generate a 941 for the quarter once you have posted pay runs in the period."
    />
  )
}

export function ContractorFilingsPage() {
  const { data, isLoading, error } = useFilings({ filing_type_prefix: 'us_1099_' })
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: TaxFiling } }) => unknown }>>(
    () => [
      { accessorKey: 'filing_type', header: 'Form', cell: FilingTypeCell },
      { accessorKey: 'tax_year', header: 'Year' },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }) => `${formatDate(row.original.period_start)} → ${formatDate(row.original.period_end)}`,
      },
      { accessorKey: 'due_date', header: 'Due', cell: DateCell('due_date') as never },
      { accessorKey: 'total_due_minor_units', header: 'Liability', cell: MoneyCell('total_due_minor_units') as never },
      { accessorKey: 'status', header: 'Status', cell: StatusCell },
      { accessorKey: 'id', header: '', cell: ViewFilingCell as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="1099 Contractors"
      description="1099-NEC, 1099-MISC, 1099-DIV, 1099-INT, 1099-K. Generated annually for contractors meeting the $600 reporting threshold (or with backup withholding)."
      icon={IdCard}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={<GenerateFilingActions kinds={[{ kind: '1099', label: 'Generate 1099 batch' }]} />}
      emptyTitle="No 1099 filings yet"
      emptyDescription="Generate the 1099 batch for every contractor with ≥$600 in posted bills for the year."
    />
  )
}

export function SalesFilingsPage() {
  return (
    <FilingsListGeneric
      title="Sales Tax Returns"
      description="State sales tax returns derived from invoiced revenue grouped by ship-to jurisdiction. Multi-state nexus tracking surfaces new filing obligations."
      filing_type="state_sales_tax"
    />
  )
}

export function BusinessFilingsPage() {
  const { data, isLoading, error } = useFilings({})
  const filtered = (data?.data ?? []).filter((f) => ['us_1120', 'us_1120s', 'us_1065', 'state_business_income'].includes(f.filing_type))
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: TaxFiling } }) => unknown }>>(
    () => [
      { accessorKey: 'filing_type', header: 'Form', cell: FilingTypeCell },
      { accessorKey: 'tax_year', header: 'Year' },
      { accessorKey: 'due_date', header: 'Due', cell: DateCell('due_date') as never },
      { accessorKey: 'total_due_minor_units', header: 'Liability', cell: MoneyCell('total_due_minor_units') as never },
      { accessorKey: 'status', header: 'Status', cell: StatusCell },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Business Income"
      description="Form 1120 (C-corp), 1120-S (S-corp), 1065 (Partnership) with K-1 generation, plus state business income. Drafted from your accounting Income Statement."
      icon={Building2}
      rows={filtered}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      emptyTitle="No business income filings yet"
      emptyDescription="Business income filings draft from your existing Income Statement and Balance Sheet. None have been generated for this period yet."
    />
  )
}

export function PersonalFilingsPage() {
  return (
    <FilingsListGeneric
      title="Personal (1040)"
      description="Personal tax returns for individual tenants - Schedule A/B/C/D/E/SE, crypto, rental, self-employed. Combines banking + invoicing + W-2/1099 data."
      filing_type="us_1040"
    />
  )
}

export function TaxPaymentsPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data, isLoading, error } = useTaxPayments()
  const [open, setOpen] = useState(false)
  const [releasingId, setReleasingId] = useState<string | null>(null)
  const canSchedule = hasPermission('tax.payments.schedule')
  const canRelease = hasPermission('tax.payments.release')

  async function onRelease(id: string) {
    setReleasingId(id)
    try {
      const r = await releasePayment(id)
      if (user?.tenantId) qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax'] })
      toast({ title: 'Payment released', description: `Confirmation ${r.data.confirmation}` })
    } catch (err) {
      toast({ title: 'Release failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setReleasingId(null)
    }
  }

  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'payment_kind', header: 'Kind' },
      {
        accessorKey: 'method',
        header: 'Method',
        cell: ({ row }) => <Badge variant="outline">{String(row.original.method).toUpperCase()}</Badge>,
      },
      {
        accessorKey: 'filing_type',
        header: 'Filing',
        cell: ({ row }) => (row.original.filing_type ? FILING_LABEL[row.original.filing_type as string] ?? (row.original.filing_type as string) : '-'),
      },
      {
        accessorKey: 'amount_minor_units',
        header: 'Amount',
        cell: MoneyCell('amount_minor_units') as never,
      },
      { accessorKey: 'scheduled_for', header: 'Scheduled', cell: DateCell('scheduled_for') as never },
      { accessorKey: 'released_at', header: 'Released', cell: DateCell('released_at') as never },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              (row.original.status === 'settled' ? 'success'
                : row.original.status === 'released' ? 'info'
                : row.original.status === 'failed' ? 'danger'
                : row.original.status === 'cancelled' ? 'secondary'
                : 'warning')
            }
          >
            {String(row.original.status)}
          </Badge>
        ),
      },
      { accessorKey: 'confirmation', header: 'Confirmation' },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => (
          canRelease && ['scheduled', 'approved'].includes(String(row.original.status)) ? (
            <Button size="sm" variant="ghost" onClick={() => onRelease(String(row.original.id))}
              disabled={releasingId === row.original.id}>
              {releasingId === row.original.id ? 'Releasing…' : 'Release'}
            </Button>
          ) : null
        ),
      },
    ],
    [canRelease, releasingId],
  )
  return (
    <SimpleListPage
      title="Tax Payments"
      description="Federal (EFTPS), state portal, and direct-ACH tax payments with scheduling, approval, transmission, and acknowledgment tracking."
      icon={Receipt}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={canSchedule ? (
        <Button onClick={() => setOpen(true)}><Plus className="mr-2 size-4" /> Schedule payment</Button>
      ) : undefined}
      emptyTitle="No tax payments scheduled"
      emptyDescription="Schedule a payment (estimated quarterly, payroll deposit, or a filing balance due) then release it to record the cash movement."
      emptyAction={canSchedule ? (
        <Button onClick={() => setOpen(true)}><Plus className="mr-2 size-4" /> Schedule payment</Button>
      ) : undefined}
      afterTable={open ? <SchedulePaymentModal open={open} onOpenChange={setOpen} /> : null}
    />
  )
}

export function TaxCalendarPage() {
  const { data, isLoading, error } = useTaxCalendar()
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'due_date', header: 'Due date', cell: DateCell('due_date') as never },
      {
        accessorKey: 'filing_type',
        header: 'Form',
        cell: ({ row }) => FILING_LABEL[row.original.filing_type as string] ?? (row.original.filing_type as string),
      },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }) =>
          `${formatDate(row.original.period_start as string)} → ${formatDate(row.original.period_end as string)}`,
      },
      {
        accessorKey: 'jurisdiction_name',
        header: 'Jurisdiction',
        cell: ({ row }) => (row.original.jurisdiction_name as string | null) ?? '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              (row.original.status === 'filed' ? 'success'
                : row.original.status === 'late' ? 'danger'
                : row.original.status === 'in_progress' ? 'warning'
                : 'outline')
            }
          >
            {String(row.original.status)}
          </Badge>
        ),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Tax Calendar"
      description="Federal, state, and local filing + payment deadlines auto-generated from your active registrations."
      icon={CalendarCheck}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      emptyTitle="No upcoming deadlines"
      emptyDescription="Once you register your tenant with a jurisdiction (federal 941, state SUI, sales tax permit), the calendar engine populates the year ahead automatically."
    />
  )
}

function RegistrationsSection() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const { data, isLoading } = useTaxRegistrations()
  const [open, setOpen] = useState(false)
  const [busyCal, setBusyCal] = useState(false)
  const canManage = hasPermission('tax.registrations.manage')
  const canCalendar = hasPermission('tax.calendar.manage')
  const rows = data?.data ?? []

  async function onGenerateCalendar() {
    setBusyCal(true)
    try {
      await generateCalendar(new Date().getFullYear())
      toast({ title: 'Filing calendar generated', description: 'Check Tax → Calendar for upcoming due dates.' })
    } catch (err) {
      toast({ title: 'Calendar generation failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusyCal(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Your registrations</h2>
          <Toolbar>
            {canCalendar ? (
              <Button variant="secondary" onClick={onGenerateCalendar} disabled={busyCal}>
                <RefreshCw className="mr-2 size-4" /> {busyCal ? 'Generating…' : 'Generate calendar'}
              </Button>
            ) : null}
            {canManage ? (
              <Button onClick={() => setOpen(true)}>
                <Plus className="mr-2 size-4" /> Register jurisdiction
              </Button>
            ) : null}
          </Toolbar>
        </div>
        {isLoading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No registrations yet. Register for federal withholding, state SUI, and sales-tax permits so the filing calendar can populate.
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border px-3 py-2">
                <span className="font-medium">{r.jurisdiction_name} <span className="text-xs text-muted-foreground">({r.jurisdiction_code})</span></span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{r.purpose.replace(/_/g, ' ')}</Badge>
                  {r.filing_frequency ? <Badge variant="secondary">{r.filing_frequency}</Badge> : null}
                  <Badge variant={r.status === 'active' ? 'success' : 'secondary'}>{r.status}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
        {open ? <RegistrationFormModal open={open} onOpenChange={setOpen} /> : null}
      </CardContent>
    </Card>
  )
}

export function JurisdictionsPage() {
  const [q, setQ] = useState('')
  const [kind, setKind] = useState('')
  const { data, isLoading, error } = useTaxJurisdictions({ q: q.trim() || undefined, kind: kind || undefined })
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'code', header: 'Code' },
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'kind',
        header: 'Kind',
        cell: ({ row }) => <Badge variant="outline">{String(row.original.kind).replace(/_/g, ' ')}</Badge>,
      },
      { accessorKey: 'country_code', header: 'Country' },
      { accessorKey: 'rounding_mode', header: 'Rounding' },
      { accessorKey: 'money_decimals', header: 'Decimals' },
      {
        accessorKey: 'is_active',
        header: 'Active',
        cell: ({ row }) => (row.original.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Inactive</Badge>),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Jurisdictions"
      description="Federal, state, county, city, and school-district tax jurisdictions. Use this catalog to register your tenant + pick rounding modes + currency decimals."
      icon={Landmark}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      beforeTable={
        <>
        <RegistrationsSection />
        <Toolbar align="start">
          <FormField label="" placeholder="Search by code or name" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" />
          <FormField
            label=""
            control={
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">All kinds</option>
                <option value="country">Country</option>
                <option value="state">State</option>
                <option value="province">Province</option>
                <option value="county">County</option>
                <option value="city">City</option>
                <option value="school_district">School district</option>
              </select>
            }
          />
        </Toolbar>
        </>
      }
      emptyTitle="No matching jurisdictions"
      emptyDescription="Adjust your filters."
    />
  )
}

export function NexusTrackerPage() {
  const { data, isLoading, error } = useNexusObservations()
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'jurisdiction_name', header: 'State' },
      { accessorKey: 'observation_date', header: 'As of', cell: DateCell('observation_date') as never },
      {
        accessorKey: 'rolling_revenue_minor_units',
        header: 'Rolling revenue (12mo)',
        cell: MoneyCell('rolling_revenue_minor_units') as never,
      },
      { accessorKey: 'rolling_transactions', header: 'Transactions' },
      {
        accessorKey: 'threshold_crossed',
        header: 'Nexus crossed',
        cell: ({ row }) => (row.original.threshold_crossed
          ? <Badge variant="danger">Crossed {formatDate(row.original.threshold_crossed_at as string)}</Badge>
          : <Badge variant="outline">Below</Badge>),
      },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Sales Tax Nexus Tracker"
      description="Rolling 12-month revenue and transaction-count tracking per state. Alerts when you cross a state's economic-nexus threshold so you can register before falling behind."
      icon={MapPin}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      emptyTitle="No nexus observations yet"
      emptyDescription="Nexus tracking kicks in once you have invoiced revenue. The tax engine recomputes monthly and writes observations here."
    />
  )
}

export function RateLookupPage() {
  const { toast } = useToast()
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [productClass, setProductClass] = useState('general')
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10))
  const [result, setResult] = useState<RateLookupResult | null>(null)
  const [loading, setLoading] = useState(false)

  async function onLookup() {
    if (!state && !zip) {
      toast({ title: 'State or ZIP required', variant: 'destructive' })
      return
    }
    setLoading(true)
    try {
      const r = await rateLookup({
        state: state.trim() || undefined,
        zip: zip.trim() || undefined,
        product_class: productClass,
        as_of: asOf,
      })
      setResult(r.data)
    } catch (err) {
      toast({
        title: 'Lookup failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page
      title="Sales Tax Rate Lookup"
      description="Resolve sales tax rate by ship-to state (or ZIP) + product class. Pulls jurisdictions from tax_jurisdictions and the active signed rule pack effective on the lookup date."
    >
      <Card>
        <CardContent className="space-y-4 p-6">
          <h2 className="text-base font-semibold">Lookup parameters</h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <FormField
              label="State (2-letter)"
              placeholder="CA"
              maxLength={2}
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            />
            <FormField
              label="ZIP (optional)"
              placeholder="94105"
              maxLength={10}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
            <FormField
              label="Product class"
              value={productClass}
              onChange={(e) => setProductClass(e.target.value)}
            />
            <FormField
              label="As of"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </div>
          <div>
            <Button onClick={onLookup} disabled={loading}>
              {loading ? 'Looking up…' : 'Look up rate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result ? (
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">Result</h2>
              <Badge variant="outline">As of {result.query.as_of}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{result.note}</p>
            {result.matched_jurisdictions.length > 0 ? (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Matched jurisdictions
                </h3>
                <ul className="space-y-1 text-sm">
                  {result.matched_jurisdictions.map((j) => (
                    <li key={j.id} className="rounded border border-border px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{j.name}</span>
                        <span className="text-xs text-muted-foreground">{j.code}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Rounds <code>{j.rounding_mode}</code> at {j.money_decimals} decimals
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.active_rule_packs.length > 0 ? (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Active rule packs
                </h3>
                <ul className="space-y-1 text-sm">
                  {result.active_rule_packs.map((p, i) => (
                    <li key={i} className="rounded border border-border px-3 py-2">
                      <code className="text-xs">{p.pack_key} v{p.pack_version}</code>
                      <span className="ml-2 text-xs text-muted-foreground">effective {p.effective_from}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Page>
  )
}

export function ExemptionCertsPage() {
  const { hasPermission } = useAuth()
  const { data, isLoading, error } = useExemptionCerts()
  const [open, setOpen] = useState(false)
  const canManage = hasPermission('tax.sales.exemptions.manage')
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'jurisdiction_name', header: 'State' },
      { accessorKey: 'customer_id', header: 'Customer' },
      { accessorKey: 'certificate_number', header: 'Certificate #' },
      { accessorKey: 'reason_kind', header: 'Reason' },
      { accessorKey: 'effective_from', header: 'From', cell: DateCell('effective_from') as never },
      { accessorKey: 'effective_to', header: 'To', cell: DateCell('effective_to') as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Exemption Certificates"
      description="Per-customer, per-jurisdiction sales tax exemption certificates. The tax engine honors these on invoices automatically."
      icon={Stamp}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={canManage ? <Button onClick={() => setOpen(true)}><Plus className="mr-2 size-4" /> Add certificate</Button> : undefined}
      afterTable={open ? <ExemptionCertModal open={open} onOpenChange={setOpen} /> : null}
      emptyTitle="No exemption certificates yet"
      emptyDescription="Add exemption certificates (resale, wholesaler, government, non-profit) so the tax engine can apply them to invoices for the relevant customer + jurisdiction."
    />
  )
}

export function TaxAssistantPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const { data: recsData, isLoading: recsLoading, refetch: refetchRecs } = useAIRecommendations('tax')
  const { data: disclaimerData } = useDisclaimer('ai-tax-assistance')
  const canUse = hasPermission('tax.assistant.use')
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [ack, setAck] = useState(false)

  async function onAsk() {
    if (!question.trim()) {
      toast({ title: 'Type a question first', variant: 'destructive' })
      return
    }
    if (!ack) {
      toast({ title: 'Acknowledge the disclaimer first', variant: 'destructive' })
      return
    }
    setAsking(true)
    try {
      const r = await askTaxAssistant(question.trim())
      toast({ title: 'AI response recorded', description: r.data.output.answer.slice(0, 80) + '…' })
      setQuestion('')
      refetchRecs()
    } catch (err) {
      toast({
        title: 'Request failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setAsking(false)
    }
  }

  return (
    <Page
      title="AI Tax Assistant"
      description="Natural-language Q&A grounded in your accounting data. Every answer carries citations to the source rows + the active disclaimer + a confidence score."
    >
      {disclaimerData?.data ? (
        <Card>
          <CardContent className="space-y-2 p-5">
            <div className="flex items-center gap-2">
              <Bot className="size-4 text-primary" />
              <h2 className="text-sm font-semibold">{disclaimerData.data.title}</h2>
              <Badge variant="outline">v{disclaimerData.data.version}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{disclaimerData.data.body_markdown}</p>
            <div className="flex items-center gap-2 pt-1 text-xs">
              <Checkbox
                id="tax-ai-disclaimer-ack"
                checked={ack}
                onCheckedChange={(v) => setAck(v === true)}
              />
              <label htmlFor="tax-ai-disclaimer-ack">I acknowledge the disclaimer and accept that AI output is not legal or tax advice.</label>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-6">
          <h2 className="text-base font-semibold">Ask a question</h2>
          {canUse ? (
            <>
              <p className="text-sm text-muted-foreground">
                Examples: <em>&quot;Why is my Q3 payroll tax higher than Q2?&quot; · &quot;How much should I set aside for Q4 estimated tax?&quot; · &quot;Which expenses look deductible this quarter?&quot;</em>
              </p>
              <FormField
                label=""
                placeholder="Type your tax question…"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button onClick={onAsk} disabled={asking || !ack}>
                  <Send className="mr-2 size-4" /> {asking ? 'Asking…' : 'Ask AI'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Output is stored under AI Recommendations below. Full chat with citations lives in Smart Chat (Bot icon, top-right).
                </span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              You need the <code>tax.assistant.use</code> permission to query the AI tax assistant.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-6">
          <h2 className="text-base font-semibold">AI recommendations history</h2>
          {recsLoading ? (
            <LoadingSpinner />
          ) : (recsData?.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No AI recommendations recorded yet. Every AI output is saved with citations + a confidence score so you can audit exactly what the model said and why.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {(recsData?.data ?? []).slice(0, 10).map((r) => {
                const out = r.output as { question?: string; answer?: string }
                return (
                  <li key={r.id} className="rounded border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{r.surface}</span>
                      <Badge variant="outline">{Number(r.confidence).toFixed(2)} confidence</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{r.model_version} · {formatDate(r.created_at)}</div>
                    {out.question ? <div className="mt-2 text-xs"><strong>Q:</strong> {out.question}</div> : null}
                    {out.answer ? <div className="mt-1 text-xs"><strong>A:</strong> {out.answer.slice(0, 600)}{out.answer.length > 600 ? '…' : ''}</div> : null}
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}

export function TaxDocumentsPage() {
  const { data, isLoading, error } = useTaxDocuments()
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      { accessorKey: 'filing_type', header: 'Filing', cell: ({ row }) => FILING_LABEL[row.original.filing_type as string] ?? (row.original.filing_type as string) },
      { accessorKey: 'tax_year', header: 'Year' },
      { accessorKey: 'kind', header: 'Kind', cell: ({ row }) => <Badge variant="outline">{String(row.original.kind)}</Badge> },
      { accessorKey: 'file_name', header: 'File' },
      { accessorKey: 'uploaded_at', header: 'Uploaded', cell: DateCell('uploaded_at') as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Tax Documents"
      description="PDFs, e-file payloads, acknowledgments, IRS correspondence, and supporting attachments - all linked to the filing they belong to."
      icon={FileText}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      emptyTitle="No tax documents yet"
      emptyDescription="When a filing is generated or transmitted, its PDF / XML / acknowledgement is archived here with KMS-SSE."
    />
  )
}

export function ReconciliationPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data, isLoading, error } = useReconciliations()
  const [busy, setBusy] = useState(false)
  const canRun = hasPermission('tax.reconciliation.run')

  async function onRun() {
    setBusy(true)
    try {
      await runReconciliation(new Date().getFullYear())
      if (user?.tenantId) qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax'] })
      toast({ title: 'Reconciliation run', description: 'Compared 941 vs W-2, ledger vs filings, and payments vs acknowledgments.' })
    } catch (err) {
      toast({ title: 'Reconciliation failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }
  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: Record<string, unknown> } }) => unknown }>>(
    () => [
      {
        accessorKey: 'reconciliation_kind',
        header: 'Kind',
        cell: ({ row }) => <Badge variant="outline">{String(row.original.reconciliation_kind).replace(/_/g, ' ')}</Badge>,
      },
      {
        accessorKey: 'period_start',
        header: 'Period',
        cell: ({ row }) => `${formatDate(row.original.period_start as string)} → ${formatDate(row.original.period_end as string)}`,
      },
      {
        accessorKey: 'ledger_total_minor_units',
        header: 'Ledger',
        cell: MoneyCell('ledger_total_minor_units') as never,
      },
      {
        accessorKey: 'filing_total_minor_units',
        header: 'Filing',
        cell: MoneyCell('filing_total_minor_units') as never,
      },
      {
        accessorKey: 'variance_minor_units',
        header: 'Variance',
        cell: ({ row }) => {
          const v = BigInt(String(row.original.variance_minor_units || '0'))
          const variant: 'success' | 'danger' | 'outline' = v === 0n ? 'success' : 'danger'
          return <Badge variant={variant}>{formatPayrollMoney(String(v), (row.original.currency as string) || 'USD')}</Badge>
        },
      },
      { accessorKey: 'created_at', header: 'Run at', cell: DateCell('created_at') as never },
    ],
    [],
  )
  return (
    <SimpleListPage
      title="Tax Reconciliation"
      description="941 quarterly sums vs W-2 annual sums, ledger vs filings, payments vs acknowledgments. The reconciliation engine flags any variance."
      icon={Scale}
      rows={data?.data}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={canRun ? (
        <Button onClick={onRun} disabled={busy}>
          <RefreshCw className="mr-2 size-4" /> {busy ? 'Running…' : 'Run reconciliation'}
        </Button>
      ) : undefined}
      emptyTitle="No reconciliations run yet"
      emptyDescription="Run a reconciliation to compare 941 quarterly sums vs W-2 annual sums, ledger vs filings, and payments vs acknowledgments."
      emptyAction={canRun ? (
        <Button onClick={onRun} disabled={busy}><RefreshCw className="mr-2 size-4" /> Run reconciliation</Button>
      ) : undefined}
    />
  )
}

export function TaxReportsPage() {
  return (
    <ReportRunner
      title="Tax Reports"
      description="Run any tax report from your filings, payments, reconciliations, and nexus data. View results inline and export to CSV or PDF."
      reports={TAX_REPORTS}
    />
  )
}

type TaxpayerRow = {
  id: string; taxpayer_kind: string; legal_name: string; display_name: string | null
  tax_id_last4: string | null; tax_id_type: string; country: string; state_region: string | null
  currency: string; is_primary: boolean; updated_at: string
}

export function TaxSettingsPage() {
  const { hasPermission } = useAuth()
  const { data, isLoading, error } = useTaxpayers()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TaxpayerRow | null>(null)
  const canManage = hasPermission('tax.settings.manage')

  const columns = useMemo<Array<{ accessorKey: string; header: string; cell?: (ctx: { row: { original: TaxpayerRow } }) => unknown }>>(
    () => [
      { accessorKey: 'legal_name', header: 'Legal name' },
      { accessorKey: 'taxpayer_kind', header: 'Kind' },
      { accessorKey: 'tax_id_type', header: 'TIN type' },
      { accessorKey: 'tax_id_last4', header: 'TIN last 4' },
      { accessorKey: 'country', header: 'Country' },
      { accessorKey: 'currency', header: 'Currency' },
      {
        accessorKey: 'is_primary',
        header: 'Primary',
        cell: ({ row }) => row.original.is_primary ? <Badge variant="success">Primary</Badge> : <Badge variant="outline">Secondary</Badge>,
      },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => canManage ? (
          <Button size="sm" variant="ghost" onClick={() => { setEditing(row.original); setOpen(true) }}>
            <Pencil className="size-4" />
          </Button>
        ) : null,
      },
    ],
    [canManage],
  )
  return (
    <SimpleListPage
      title="Tax Settings"
      description="Filing entity (taxpayer) records: legal name, EIN, signing officer, and primary-vs-secondary entities for multi-entity tenants."
      icon={Settings}
      rows={(data?.data as TaxpayerRow[] | undefined)}
      isLoading={isLoading}
      error={error}
      columns={columns as never}
      actions={canManage ? (
        <Button onClick={() => { setEditing(null); setOpen(true) }}>
          <Plus className="mr-2 size-4" /> Add taxpayer
        </Button>
      ) : undefined}
      emptyTitle="No taxpayer set up yet"
      emptyDescription="Configure your tenant's filing entity (legal name, EIN, address) so the filing engine can populate forms correctly."
      emptyAction={canManage ? (
        <Button onClick={() => { setEditing(null); setOpen(true) }}>
          <Plus className="mr-2 size-4" /> Add taxpayer
        </Button>
      ) : undefined}
      afterTable={open ? (
        <TaxpayerFormModal open={open} onOpenChange={setOpen} editing={editing} />
      ) : null}
    />
  )
}

